import {AxiosRequestConfig} from 'axios';
import {fileCatalystClient} from '.';
import {
  convertToDoubleBackslashes,
  getFileNameFromPath,
} from '../utils/fcUploadFileUtils';

export const fcDefaultConfig = {
  name: '',
  remoteServer: '', // Required
  remotePort: '', // Required
  username: '', // Required
  password: '', // Required
  usesSSL: '', // Required
  initialDirectory: '',
  lockInitialDirectory: 'false',
  servletURL: '',
  connectionMode: '-1',
  defaultIssuerURL: '',
};

const fcDefaultUploadConfig: Record<string, unknown> = {
  SourceConnectionKey: 'local',
  LocalWorkingDirectory: '',
  transferConfiguration: {
    OverwriteOption: 'YES_TO_ALL',
    useCompression: false,
    UseCongestionControl: true,
    VerifyIntegrity: false,
    SendEmailNotification: true,
    EmailAddress: '',
    EmailBody: '',
    SentFilelistInEmail: false,
    SendEmailNotificationPerFile: false,
    RemoteDirectory: '',
    AutoResume: false,
    AutoZip: false,
  },
};

export const getConnectionKey = (
  retryConfig?: AxiosRequestConfig,
  signal?: AbortSignal
) => {
  const params = {...fcDefaultConfig};
  return fileCatalystClient.get(`agent/connect`, {
    ...retryConfig,
    params,
    ...(signal ? {signal} : {}),
  });
};

export const getConnectionKeyPostRequest = (
  encryptedPayload: string,
  retryConfig?: AxiosRequestConfig,
  signal?: AbortSignal
) => {
  const requestBody = {
    MungedConnectionRequest: encryptedPayload,
  };
  return fileCatalystClient.post(`agent/connect`, requestBody, {
    ...retryConfig,
    ...(signal ? {signal} : {}),
  });
};

export const deleteConnectionKey = (connectionKey: string) => {
  return fileCatalystClient.delete(`agent/connect/${connectionKey}/removeKey`);
};

export const getConnectionTest = (
  connectionKey: string,
  retryConfig?: AxiosRequestConfig,
  signal?: AbortSignal
) => {
  return fileCatalystClient.get(
    `agent/connect/connectivityTest/${connectionKey}`,
    {
      ...retryConfig,
      ...(signal ? {signal} : {}),
    }
  );
};

export const getRemoteFilesUsingConnectionId = (
  connectionKey: string,
  retryConfig?: AxiosRequestConfig
) => {
  const params = {connectionKey, isInitial: 'true'};
  return fileCatalystClient.get(`files/remote`, {...retryConfig, params});
};

export const getAgentHeartbeat = (
  retryConfig?: AxiosRequestConfig,
  signal?: AbortSignal
) => {
  return fileCatalystClient.get(`agent/heartbeat`, {
    ...retryConfig,
    ...(signal ? {signal} : {}),
  });
};

export const getLocalDir = (path = '', signal?: AbortSignal) => {
  const params = {connectionKey: 'local', sortby: 'name'};
  return fileCatalystClient.get(`files/local${path}`, {
    params,
    ...(signal ? {signal} : {}),
  });
};

export const getLocalFile = (path: string) => {
  return fileCatalystClient.get(`files/localfile/${path}`);
};

export const deleteRemoteFile = (connectionKey: string, filePath: string) => {
  const payload = {connectionKey, ids: [filePath]};
  return fileCatalystClient.delete(`files/remotefile`, {
    data: payload,
  });
};

export const getJobIdForMultipleUpload = (
  connectionKey: string,
  remoteDirectory: string,
  fileList: string[],
  retryConfig?: AxiosRequestConfig
) => {
  const requestBody = {
    fileList,
    DestinationConnectionKey: connectionKey,
    ...fcDefaultUploadConfig,
    transferConfiguration: {
      ...(fcDefaultUploadConfig?.transferConfiguration as object),
      RemoteDirectory: remoteDirectory,
    },
  };
  return fileCatalystClient.post(`files/transferRev1`, requestBody, {
    ...retryConfig,
  });
};

export const getJobIdForSingleUpload = (
  connectionKey: string,
  remoteDirectory: string,
  sourceFile: string,
  retryConfig?: AxiosRequestConfig
) => {
  const requestBody = {
    sourceFile: convertToDoubleBackslashes(sourceFile),
    destinationFile: `${remoteDirectory}/${getFileNameFromPath(sourceFile)}`,
    DestinationConnectionKey: connectionKey,
    ...fcDefaultUploadConfig,
    transferConfiguration: {
      ...(fcDefaultUploadConfig?.transferConfiguration as object),
      RemoteDirectory: remoteDirectory,
    },
  };
  return fileCatalystClient.put(`files/transferRev1`, requestBody, {
    ...retryConfig,
  });
};

export const checkTransferStatus = (
  transferId: string,
  signal?: AbortSignal,
  retryConfig?: AxiosRequestConfig
) => {
  return fileCatalystClient.get(`agent/status/${transferId}`, {
    ...retryConfig,
    ...(signal ? {signal} : {}),
  });
};

export const checkAllTransferStatus = (signal?: AbortSignal) => {
  return fileCatalystClient.get(`agent/status`, {
    ...(signal ? {signal} : {}),
  });
};

export const cancelFileTransfer = (transferID = '') => {
  const params = {transferID};
  return fileCatalystClient.get(`files/transferRev1/cancel`, {params});
};
